import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import CustomerFormSection from './sections/CustomerFormSection';
import styled from 'styled-components';
import { IoChevronDown, IoPersonOutline, IoSendOutline } from 'react-icons/io5';
import InfoTooltip from '../../../components/common/InfoTooltip';
import { requestShare } from '../../../bridge/webBridge';
import { useAuth } from '../../../context/AuthContext';
import { getRequestsByRealtor } from '../../../services/serviceRequestService';
import { getCommissionSummary } from '../../../services/commissionService';
import { getNotices } from '../../../services/noticeService';

const buildSignupUrl = (ref) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://ieum-8fd99.web.app';
  return `${origin}/realtor/signup?ref=${ref || 'demo'}`;
};

// ───── 스타일 ─────

const TabBarWrap = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  background: ${({ theme }) => theme.colors.primary};
  padding: 0 ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.base};
  position: sticky;
  top: 0;
  z-index: 40;
`;

const TabBarHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing.base};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TabBarTitleArea = styled.div``;

const TabBarTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  margin-bottom: 2px;
`;

const TabBarDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.85);
`;


const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const TabRow = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const TabLink = styled.button`
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.pill};
  transition: all 0.15s;
  cursor: pointer;
  pointer-events: auto;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
`;

const Content = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.realtor.sectionGap};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const FormCard = styled.div`
  background: #F3F4F6;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const MethodTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const MethodTab = styled.button`
  padding: 6px 14px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;

const ContactLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PhoneInput = styled.input`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  box-sizing: border-box;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const InviteBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  background: rgba(36, 140, 255, 0.2);
  color: ${({ theme }) => theme.colors.primary};

  &:hover { background: rgba(36, 140, 255, 0.3); }
`;

/* 상태 그리드 (회색 카드 + 흰 셀) */
const StatsCard = styled.div`
  background: #F1F5FF;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols || 3}, 1fr)`};
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatCell = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 8px 4px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatLabelWrap = styled.div`
  height: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  line-height: 1.3;
`;

const StatSub = styled.span`
  font-size: 7px;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1;
  white-space: nowrap;
`;

const StatValue = styled.p`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 2px;
`;

/* 정산금 현황 */
const SettlementCard = styled.div`
  background: linear-gradient(180deg, #036EB8 0%, #0A285F 100%);
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const SettlementTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SettlementRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SettlementItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => theme.spacing.md};
`;

const SettlementItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  white-space: nowrap;
`;

const SettlementItemLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
`;

const SettlementWithdrawBtn = styled.button`
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SettlementItemValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  text-align: right;
`;

/* 공지사항 */
const NoticeCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 0 ${({ theme }) => theme.spacing.lg};
`;

const NoticeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
  &:last-child { border-bottom: none; }
`;

const NoticeLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const NoticeDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 2px;
`;

const NoticeTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
`;

const NoticeTag = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const NoticeArrow = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const NoticeEmpty = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

// ───── 컴포넌트 ─────
const InviteRealtor = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCustomerActive = pathname === '/realtor/recommend' || pathname === '/realtor/recommend/customer';
  const isRealtorActive = pathname === '/realtor/recommend/realtor';
  const isQrActive = pathname === '/realtor/recommend/qr';
  const { userData } = useAuth();
  const [method, setMethod] = useState('direct');
  const [phone, setPhone] = useState('');
  const [requests, setRequests] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState({ withdrawable: 0, conversion: 0, referral: 0, settled: 0 });
  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(true);
  const signupUrl = buildSignupUrl(userData?.referralCode || userData?.uid);

  useEffect(() => {
    if (!userData?.uid) return;
    getRequestsByRealtor(userData.uid).then(setRequests).catch(() => setRequests([]));
    getCommissionSummary(userData.uid).then(setCommissionSummary).catch(() => {});
  }, [userData?.uid]);

  useEffect(() => {
    setNoticesLoading(true);
    getNotices(1, 5)
      .then((rows) => setNotices(rows || []))
      .catch(() => setNotices([]))
      .finally(() => setNoticesLoading(false));
  }, []);

  const customerStats = useMemo(() => {
    const buckets = { 접수: 0, 진행중: 0, 보류: 0, 취소: 0, 완료: 0 };
    requests.forEach((r) => {
      const s = r.hq_status || r.hqStatus || 'unread';
      if (r.serviceCompleted || s === 'settlement_done' || s === 'completed') buckets['완료'] += 1;
      else if (s === 'unread' || s === 'read') buckets['접수'] += 1;
      else if (s === 'assigned') buckets['진행중'] += 1;
      else if (s === 'settlement_check' || s === 'hq_review_needed') buckets['보류'] += 1;
      else if (s === 'cancelled' || s === 'unmatched_cancelled') buckets['취소'] += 1;
    });
    return [
      { label: '접수', value: buckets['접수'] },
      { label: '진행중', value: buckets['진행중'] },
      { label: '보류', value: buckets['보류'] },
      { label: '취소', value: buckets['취소'] },
      { label: '완료', value: buckets['완료'] },
    ];
  }, [requests]);

  const settlementItems = useMemo(() => ([
    { label: '마케팅 리워드', value: commissionSummary.conversion || 0 },
    { label: '추천 수익금', value: commissionSummary.referral || 0 },
    { label: '출금 가능', value: commissionSummary.withdrawable || 0, hasBtn: true },
    { label: '실수익 합계', value: commissionSummary.settled || 0 },
  ]), [commissionSummary]);

  // 주소록에서 선택한 연락처 수신
  React.useEffect(() => {
    const handler = (event) => {
      let data;
      try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch { return; }
      if (data?.type === 'CONTACTS_RESULT' && data?.payload?.success) {
        const contacts = data.payload.contacts || [];
        if (contacts.length > 0) {
          setPhone(contacts[0].phone.replace(/\D/g, ''));
          setMethod('contacts');
        }
      }
    };
    window.addEventListener('message', handler);
    document.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      document.removeEventListener('message', handler);
    };
  }, []);

  const handleInvite = () => {
    if (!phone.replace(/\D/g, '')) {
      alert('휴대폰 번호를 입력해주세요.');
      return;
    }
    const rawPhone = phone.replace(/\D/g, '');
    const msg = `이음에 함께하세요! 가입하시면 추천인이 자동 등록됩니다.\n${signupUrl}`;
    window.location.href = `sms:${rawPhone}?body=${encodeURIComponent(msg)}`;
  };

  return (
    <div>
      <TabBarWrap>
        <TabBarHeader>
          <TabBarTitleArea>
            <TabBarTitle>추천하기</TabBarTitle>
            <TabBarDesc>고객·동료 중개사에게 문자/카카오 발송</TabBarDesc>
          </TabBarTitleArea>

        </TabBarHeader>
      </TabBarWrap>

      <TabRow>
        <TabLink type="button" onClick={() => navigate('/realtor/recommend/customer')} $active={isCustomerActive}>고객 초대</TabLink>
        <TabLink type="button" onClick={() => navigate('/realtor/recommend/realtor')} $active={isRealtorActive}>공인중개사 초대</TabLink>
        <TabLink type="button" onClick={() => navigate('/realtor/recommend/qr')} $active={isQrActive}>전용 QR</TabLink>
      </TabRow>

      <Content>
        <CustomerFormSection />

        {/* 고객 초대 현황 */}
        <section>
          <StatsCard>
            <SectionTitle>고객 초대 현황 <InfoTooltip text="신청한 고객들의 상황이 실시간으로 표시됩니다. 해당 테이블의 기여도로 마케팅 수익금이 정산됩니다. 단, 정책상 고객의 개인정보는 열람할 수 없으니 참고해주세요." /></SectionTitle>
            <StatsGrid $cols={2}>
              {customerStats.slice(0, 2).map((s, i) => (
                <StatCell key={i}>
                  <StatLabelWrap><StatLabel>{s.label}</StatLabel></StatLabelWrap>
                  <StatValue>{s.value}</StatValue>
                </StatCell>
              ))}
            </StatsGrid>
            <StatsGrid $cols={3} style={{ marginTop: 8 }}>
              {customerStats.slice(2).map((s, i) => (
                <StatCell key={i}>
                  <StatLabelWrap><StatLabel>{s.label}</StatLabel></StatLabelWrap>
                  <StatValue>{s.value}</StatValue>
                </StatCell>
              ))}
            </StatsGrid>
          </StatsCard>
        </section>

        {/* 정산금 현황 */}
        <section>
          <SettlementCard>
            <SettlementTitle>정산금 현황</SettlementTitle>
            <SettlementRow>
              {settlementItems.map((item) => (
                <SettlementItem key={item.label}>
                  <SettlementItemTop>
                    <SettlementItemLabel>{item.label}</SettlementItemLabel>
                    {item.hasBtn && (
                      <SettlementWithdrawBtn onClick={() => navigate('/realtor/mypage/withdraw')}>
                        출금
                      </SettlementWithdrawBtn>
                    )}
                  </SettlementItemTop>
                  <SettlementItemValue>{(item.value || 0).toLocaleString()}원</SettlementItemValue>
                </SettlementItem>
              ))}
            </SettlementRow>
          </SettlementCard>
        </section>

        <section>
          <NoticeCard>
            <SectionTitle style={{ paddingTop: 20 }}>공지사항</SectionTitle>
            {noticesLoading ? (
              <NoticeEmpty>불러오는 중...</NoticeEmpty>
            ) : notices.length === 0 ? (
              <NoticeEmpty>등록된 공지가 없습니다.</NoticeEmpty>
            ) : (
              notices.map((n) => (
                <NoticeItem key={n.id} onClick={() => navigate('/realtor/mypage/notice')}>
                  <NoticeLeft>
                    <NoticeDate>{n.date}</NoticeDate>
                    <NoticeTitle><NoticeTag>[공지]</NoticeTag> {n.title}</NoticeTitle>
                  </NoticeLeft>
                  <NoticeArrow><IoChevronDown size={16} /></NoticeArrow>
                </NoticeItem>
              ))
            )}
          </NoticeCard>
        </section>
      </Content>
    </div>
  );
};

export default InviteRealtor;
